<script setup lang="ts">
import { computed } from 'vue'
import { useUiStore, type SelectionMode } from '@/stores/uiStore'
import { useI18n, type I18nKey } from '@/i18n'
import TextControls from '@/components/panels/TextControls.vue'
import { useDocumentStore } from '@/stores/documentStore'
import { runCommand } from '@/commands/registry'
import { useBrushStore } from '@/brush/presets'
import GradientEditor from '@/components/common/GradientEditor.vue'
import { guideFor, tx } from '@/help/toolGuides'

const ui = useUiStore()
const docStore = useDocumentStore()
const { t } = useI18n()
const activeText = computed(() => (docStore.activeLayer?.kind === 'text' ? docStore.activeLayer : null))

const toolLabel = computed(() => t(`tool.${ui.effectiveTool}` as I18nKey))
const guideSummary = computed(() => tx(guideFor(ui.effectiveTool).summary))
const isSelectionTool = computed(() => ['marquee', 'lasso', 'wand', 'quickSelect'].includes(ui.activeTool))
const isBrushTool = computed(() => ['brush', 'eraser'].includes(ui.activeTool))
const brushSettings = computed(() => (ui.activeTool === 'eraser' ? ui.eraser : ui.brush))
const brushStore = useBrushStore()
function toggleCustomGradient(on: boolean) {
  ui.toolOptions.gradientStops = on ? [{ pos: 0, hex: ui.foreground, alpha: 1 }, { pos: 1, hex: ui.background, alpha: 1 }] : null
}
function onPreset(v: string) {
  if (v === '__save') {
    const name = prompt(t('options.preset.save'), `${t('options.preset')} ${brushStore.presets.length + 1}`)
    if (name) brushStore.savePreset(name, brushSettings.value)
  } else if (v === '__delete') {
    const names = brushStore.presets.map((p, i) => `${i + 1}: ${p.name}`).join('\n')
    const idx = Number(prompt(`${t('options.preset.delete')}\n${names}`, '1'))
    const p = brushStore.presets[idx - 1]
    if (p) brushStore.removePreset(p.id)
  } else if (v) brushStore.applyPreset(v)
}
function pct(v: number) {
  return Math.round(v * 100)
}
const alignButtons: { mode: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom'; label: I18nKey; glyph: string }[] = [
  { mode: 'left', label: 'align.left', glyph: '⇤' },
  { mode: 'hcenter', label: 'align.hcenter', glyph: '⇹' },
  { mode: 'right', label: 'align.right', glyph: '⇥' },
  { mode: 'top', label: 'align.top', glyph: '⤒' },
  { mode: 'vcenter', label: 'align.vcenter', glyph: '⇳' },
  { mode: 'bottom', label: 'align.bottom', glyph: '⤓' }
]
const canAlign = computed(() => (docStore.doc?.activeLayerIds.length ?? 0) >= 1)
const canDistribute = computed(() => (docStore.doc?.activeLayerIds.length ?? 0) >= 3)
function align(mode: (typeof alignButtons)[number]['mode']) {
  void runCommand('layer.align', { mode })
}
function distribute(axis: 'h' | 'v') {
  void runCommand('layer.distribute', { axis })
}
const modes: { id: SelectionMode; label: I18nKey; glyph: string }[] = [
  { id: 'new', label: 'options.mode.new', glyph: '▢' },
  { id: 'add', label: 'options.mode.add', glyph: '⊕' },
  { id: 'subtract', label: 'options.mode.subtract', glyph: '⊖' },
  { id: 'intersect', label: 'options.mode.intersect', glyph: '⊗' }
]
</script>

<template>
  <div class="options">
    <span class="tool">{{ toolLabel }}</span>
    <template v-if="ui.activeTool === 'move'">
      <span class="sep" />
      <button v-for="a in alignButtons" :key="a.mode" class="mode" :title="t(a.label)" :disabled="!canAlign" @click="align(a.mode)">{{ a.glyph }}</button>
      <span class="sep" />
      <button class="mode" :title="t('align.distributeH')" :disabled="!canDistribute" @click="distribute('h')">⋯</button>
      <button class="mode" :title="t('align.distributeV')" :disabled="!canDistribute" @click="distribute('v')">⋮</button>
    </template>
    <template v-if="isSelectionTool">
      <span class="sep" />
      <button v-for="m in modes" :key="m.id" class="mode" :class="{ active: ui.toolOptions.selectionMode === m.id }" :title="t(m.label)" @click="ui.toolOptions.selectionMode = m.id">{{ m.glyph }}</button>
      <span class="sep" />
      <template v-if="ui.activeTool === 'marquee'">
        <select v-model="ui.toolOptions.marqueeShape">
          <option value="rect">{{ t('options.shape.rect') }}</option>
          <option value="ellipse">{{ t('options.shape.ellipse') }}</option>
        </select>
      </template>
      <template v-if="ui.activeTool === 'lasso'">
        <select v-model="ui.toolOptions.lassoKind">
          <option value="free">{{ t('options.lasso.free') }}</option>
          <option value="polygon">{{ t('options.lasso.polygon') }}</option>
          <option value="magnetic">{{ t('options.lasso.magnetic') }}</option>
        </select>
      </template>
      <template v-if="ui.activeTool === 'quickSelect'">
        <label class="row"><span class="muted">{{ t('options.size') }}</span><input v-model.number="ui.toolOptions.quickSize" type="number" min="2" max="500" class="num" /> px</label>
        <label class="row"><span class="muted">{{ t('options.tolerance') }}</span><input v-model.number="ui.toolOptions.quickTolerance" type="number" min="1" max="255" class="num" /></label>
        <label class="row"><input v-model="ui.toolOptions.quickSampleAll" type="checkbox" /><span>{{ t('options.sampleAll') }}</span></label>
      </template>
      <template v-else-if="ui.activeTool === 'wand'">
        <label class="row"><span class="muted">{{ t('options.tolerance') }}</span><input v-model.number="ui.toolOptions.wandTolerance" type="number" min="0" max="255" class="num" /></label>
        <label class="row"><input v-model="ui.toolOptions.wandContiguous" type="checkbox" /><span>{{ t('options.contiguous') }}</span></label>
        <label class="row"><input v-model="ui.toolOptions.wandSampleAll" type="checkbox" /><span>{{ t('options.sampleAll') }}</span></label>
      </template>
      <template v-else>
        <label class="row"><span class="muted">{{ t('options.feather') }}</span><input v-model.number="ui.toolOptions.feather" type="number" min="0" max="250" class="num" /> px</label>
        <label class="row"><input v-model="ui.toolOptions.antialias" type="checkbox" /><span>{{ t('options.antialias') }}</span></label>
      </template>
    </template>
    <template v-if="isBrushTool">
      <span class="sep" />
      <label class="row"><span class="muted">{{ t('options.size') }}</span><input v-model.number="brushSettings.size" type="number" min="1" max="2000" class="num" /> px</label>
      <label class="row"><span class="muted">{{ t('options.hardness') }}</span><input :value="pct(brushSettings.hardness)" type="range" min="0" max="100" class="slider" @input="brushSettings.hardness = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(brushSettings.hardness) }}%</span></label>
      <label class="row"><span class="muted">{{ t('options.opacity') }}</span><input :value="pct(brushSettings.opacity)" type="range" min="1" max="100" class="slider" @input="brushSettings.opacity = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(brushSettings.opacity) }}%</span></label>
      <label class="row"><span class="muted">{{ t('options.flow') }}</span><input :value="pct(brushSettings.flow)" type="range" min="1" max="100" class="slider" @input="brushSettings.flow = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(brushSettings.flow) }}%</span></label>
      <label class="row"><span class="muted">{{ t('options.smoothing') }}</span><input v-model.number="brushSettings.smoothing" type="number" min="0" max="50" class="num" /></label>
      <label v-if="ui.activeTool === 'brush'" class="row"><input v-model="brushSettings.pencil" type="checkbox" /><span>{{ t('options.pencil') }}</span></label>
      <label class="row"><input v-model="brushSettings.pressureSize" type="checkbox" /><span>{{ t('options.pressureSize') }}</span></label>
      <span class="sep" />
      <select v-model="brushSettings.tipId" :title="t('options.tip')">
        <option value="round">{{ t('options.tip.round') }}</option>
        <option v-for="tp in brushStore.tips" :key="tp.id" :value="tp.id">{{ tp.name }}</option>
      </select>
      <label class="row"><span class="muted">{{ t('options.angle') }}</span><input v-model.number="brushSettings.angle" type="number" min="-180" max="180" class="num" /></label>
      <label class="row"><span class="muted">{{ t('options.roundness') }}</span><input :value="pct(brushSettings.roundness)" type="range" min="5" max="100" class="slider short" @input="brushSettings.roundness = Number(($event.target as HTMLInputElement).value) / 100" /></label>
      <label class="row"><span class="muted">{{ t('options.scatter') }}</span><input :value="pct(brushSettings.scatter)" type="range" min="0" max="300" class="slider short" @input="brushSettings.scatter = Number(($event.target as HTMLInputElement).value) / 100" /></label>
      <label class="row"><span class="muted">{{ t('options.sizeJitter') }}</span><input :value="pct(brushSettings.sizeJitter)" type="range" min="0" max="100" class="slider short" @input="brushSettings.sizeJitter = Number(($event.target as HTMLInputElement).value) / 100" /></label>
      <select :title="t('options.preset')" :value="''" @change="onPreset(($event.target as HTMLSelectElement).value)">
        <option value="">{{ t('options.preset') }}</option>
        <option v-for="p in brushStore.presets" :key="p.id" :value="p.id">{{ p.name }}</option>
        <option value="__save">{{ t('options.preset.save') }}</option>
        <option v-if="brushStore.presets.length" value="__delete">{{ t('options.preset.delete') }}</option>
      </select>
    </template>
    <template v-if="ui.activeTool === 'fill'">
      <span class="sep" />
      <label class="row"><span class="muted">{{ t('options.tolerance') }}</span><input v-model.number="ui.toolOptions.fillTolerance" type="number" min="0" max="255" class="num" /></label>
      <label class="row"><input v-model="ui.toolOptions.fillContiguous" type="checkbox" /><span>{{ t('options.contiguous') }}</span></label>
      <label class="row"><input v-model="ui.toolOptions.fillSampleAll" type="checkbox" /><span>{{ t('options.sampleAll') }}</span></label>
      <label class="row"><span class="muted">{{ t('options.opacity') }}</span><input :value="pct(ui.toolOptions.fillOpacity)" type="range" min="1" max="100" class="slider" @input="ui.toolOptions.fillOpacity = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(ui.toolOptions.fillOpacity) }}%</span></label>
    </template>
    <template v-if="ui.activeTool === 'gradient'">
      <span class="sep" />
      <select v-model="ui.toolOptions.gradientStyle">
        <option v-for="st in ['linear', 'radial', 'angle', 'reflected', 'diamond']" :key="st" :value="st">{{ t(`options.gradient.${st}` as I18nKey) }}</option>
      </select>
      <label class="row"><span class="muted">{{ t('options.opacity') }}</span><input :value="pct(ui.toolOptions.gradientOpacity)" type="range" min="1" max="100" class="slider" @input="ui.toolOptions.gradientOpacity = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(ui.toolOptions.gradientOpacity) }}%</span></label>
      <label class="row"><input v-model="ui.toolOptions.gradientToTransparent" type="checkbox" /><span>{{ t('options.toTransparent') }}</span></label>
      <label class="row"><input :checked="!!ui.toolOptions.gradientStops" type="checkbox" @change="toggleCustomGradient(($event.target as HTMLInputElement).checked)" /><span>{{ t('options.gradientCustom') }}</span></label>
      <GradientEditor v-if="ui.toolOptions.gradientStops" class="inline-ge" :stops="ui.toolOptions.gradientStops" with-alpha @input="st => (ui.toolOptions.gradientStops = st.map(s => ({ pos: s.pos, hex: s.hex, alpha: s.alpha ?? 1 })))" @change="st => (ui.toolOptions.gradientStops = st.map(s => ({ pos: s.pos, hex: s.hex, alpha: s.alpha ?? 1 })))" />
      <label class="row"><input v-model="ui.toolOptions.gradientReverse" type="checkbox" /><span>{{ t('options.reverse') }}</span></label>
      <label class="row"><input v-model="ui.toolOptions.gradientDither" type="checkbox" /><span>{{ t('options.dither') }}</span></label>
    </template>
    <template v-if="ui.isRetouchTool(ui.activeTool)">
      <span class="sep" />
      <label class="row"><span class="muted">{{ t('options.size') }}</span><input v-model.number="ui.retouch.size" type="number" min="1" max="2000" class="num" /> px</label>
      <label class="row"><span class="muted">{{ t('options.hardness') }}</span><input :value="pct(ui.retouch.hardness)" type="range" min="0" max="100" class="slider" @input="ui.retouch.hardness = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(ui.retouch.hardness) }}%</span></label>
      <label class="row"><span class="muted">{{ t('options.flow') }}</span><input :value="pct(ui.retouch.flow)" type="range" min="1" max="100" class="slider" @input="ui.retouch.flow = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(ui.retouch.flow) }}%</span></label>
      <template v-if="ui.activeTool === 'dodge' || ui.activeTool === 'burn'">
        <label class="row"><span class="muted">{{ t('options.exposure') }}</span><input :value="pct(ui.retouch.exposure)" type="range" min="1" max="100" class="slider" @input="ui.retouch.exposure = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(ui.retouch.exposure) }}%</span></label>
      </template>
      <template v-else-if="ui.activeTool === 'sponge'">
        <select v-model="ui.retouch.spongeDesaturate"><option :value="true">{{ t('options.desaturate') }}</option><option :value="false">{{ t('options.saturate') }}</option></select>
      </template>
      <template v-else-if="ui.activeTool === 'clone' || ui.activeTool === 'heal'">
        <label class="row"><input v-model="ui.retouch.cloneAligned" type="checkbox" /><span>{{ t('options.aligned') }}</span></label>
        <span class="muted">{{ ui.cloneSource ? `${Math.round(ui.cloneSource.x)}, ${Math.round(ui.cloneSource.y)}` : t('clone.setSource') }}</span>
      </template>
      <template v-else>
        <label class="row"><span class="muted">{{ t('options.strength') }}</span><input :value="pct(ui.retouch.strength)" type="range" min="1" max="100" class="slider" @input="ui.retouch.strength = Number(($event.target as HTMLInputElement).value) / 100" /><span class="mono pct">{{ pct(ui.retouch.strength) }}%</span></label>
      </template>
    </template>
    <template v-if="ui.activeTool === 'shape'">
      <span class="sep" />
      <select v-model="ui.toolOptions.shapeType">
        <option v-for="st in ['rect', 'ellipse', 'polygon', 'line']" :key="st" :value="st">{{ t(`shape.${st}` as I18nKey) }}</option>
      </select>
      <label v-if="ui.toolOptions.shapeType === 'rect'" class="row"><span class="muted">{{ t('shape.radius') }}</span><input v-model.number="ui.toolOptions.shapeRadius" type="number" min="0" max="1000" class="num" /></label>
      <template v-if="ui.toolOptions.shapeType === 'polygon'">
        <label class="row"><span class="muted">{{ t('shape.sides') }}</span><input v-model.number="ui.toolOptions.shapeSides" type="number" min="3" max="100" class="num" /></label>
        <label class="row"><span class="muted">{{ t('shape.star') }}</span><input v-model.number="ui.toolOptions.shapeStar" type="number" min="0" max="0.95" step="0.05" class="num" /></label>
      </template>
      <label class="row"><input v-model="ui.toolOptions.shapeFill" type="checkbox" /><span>{{ t('shape.fill') }}</span></label>
      <label class="row"><input v-model="ui.toolOptions.shapeStroke" type="checkbox" /><span>{{ t('shape.stroke') }}</span></label>
      <label class="row"><span class="muted">{{ t('shape.strokeWidth') }}</span><input v-model.number="ui.toolOptions.shapeStrokeWidth" type="number" min="0" max="500" class="num" /></label>
    </template>
    <template v-if="ui.activeTool === 'text'">
      <span class="sep" />
      <TextControls :layer="activeText" compact />
      <button v-if="ui.editingTextId" class="primary" @click="ui.modalToolCommand = 'commit'">{{ t('text.commit') }}</button>
    </template>
    <template v-if="ui.modalTool === 'transform' && ui.transformState">
      <span class="sep" />
      <label class="row"><span class="muted">W</span><input :value="Math.round(ui.transformState.scaleX * 1000) / 10" type="number" step="1" class="num" @change="ui.transformState.scaleX = Number(($event.target as HTMLInputElement).value) / 100" /> %</label>
      <label class="row"><span class="muted">H</span><input :value="Math.round(ui.transformState.scaleY * 1000) / 10" type="number" step="1" class="num" @change="ui.transformState.scaleY = Number(($event.target as HTMLInputElement).value) / 100" /> %</label>
      <select v-if="ui.transformState.canDistort" :value="ui.transformState.mode" @change="ui.transformState.mode = ($event.target as HTMLSelectElement).value as 'free'">
        <option value="free">{{ t('transform.mode.free') }}</option>
        <option value="distort">{{ t('transform.mode.distort') }}</option>
        <option value="perspective">{{ t('transform.mode.perspective') }}</option>
        <option value="warp">{{ t('transform.mode.warp') }}</option>
      </select>
      <label class="row"><span class="muted">{{ t('transform.angle') }}</span><input :value="Math.round((ui.transformState.angle * 180) / Math.PI * 10) / 10" type="number" step="1" class="num" @change="ui.transformState.angle = (Number(($event.target as HTMLInputElement).value) * Math.PI) / 180" /> °</label>
      <button class="primary" @click="ui.modalToolCommand = 'commit'">{{ t('transform.commit') }}</button>
      <button @click="ui.modalToolCommand = 'cancel'">{{ t('transform.cancel') }}</button>
    </template>
    <template v-if="ui.modalTool === 'pen'">
      <span class="sep" />
      <span class="muted">{{ t('pen.hint') }}</span>
      <button class="primary" @click="ui.modalToolCommand = 'commit'">{{ t('pen.commit') }}</button>
      <button @click="ui.modalToolCommand = 'cancel'">{{ t('pen.cancel') }}</button>
    </template>
    <template v-else-if="ui.modalTool === 'crop'">
      <span class="sep" />
      <button class="primary" @click="ui.modalToolCommand = 'commit'">{{ t('crop.commit') }}</button>
      <button @click="ui.modalToolCommand = 'cancel'">{{ t('crop.cancel') }}</button>
    </template>
    <span class="spacer" />
    <span class="guide-hint muted" :title="guideSummary">{{ guideSummary }}</span>
    <button class="help" :class="{ on: ui.toolGuideOpen }" :title="t('guide.open')" @click="ui.toolGuideOpen = !ui.toolGuideOpen">?</button>
  </div>
</template>

<style scoped>
.guide-hint {
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
}
.help {
  width: 22px;
  height: 20px;
  padding: 0;
  flex: none;
  font-weight: 600;
}
.help.on {
  background: var(--accent);
  color: #fff;
}
@media (max-width: 1100px) {
  .guide-hint {
    display: none;
  }
}
.options {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  background: var(--bg-1);
  border-top: 1px solid var(--line);
  white-space: nowrap;
  overflow: hidden;
}
.tool {
  font-weight: 600;
  color: var(--fg-1);
}
.sep {
  width: 1px;
  height: 18px;
  background: var(--line-strong);
  margin: 0 4px;
}
.mode {
  width: 26px;
  height: 24px;
  padding: 0;
  font-size: 14px;
  background: transparent;
  border-color: transparent;
}
.mode.active {
  background: var(--bg-3);
  border-color: var(--line-strong);
  color: var(--accent-hi);
}
.num {
  width: 56px;
}
.slider {
  width: 70px;
}
.pct {
  width: 34px;
  font-size: 11px;
}
.slider.short {
  width: 60px;
}
.inline-ge {
  min-width: 180px;
  max-width: 260px;
}
</style>
